import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { enhancedApi, mapApiErrorsToForm } from '../api';
import { runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { fieldHelperText } from '../utils/formHelpers';
import { messageFormSchema } from './MessageForm';

const MAX_CHARS = 5000;
const POLL_INTERVAL_MS = 10000;

function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function MessagesTab({ client, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const {
    errors: msgErrors,
    touched: msgTouched,
    validateField: msgValidateField,
    touchField: msgTouchField,
    validateAll: msgValidateAll,
    clearErrors: msgClearErrors,
    mergeServerFieldErrors: msgMergeServerErrors,
  } = useFormValidation(messageFormSchema);

  const msgFormValues = useMemo(() => ({ content: messageText }), [messageText]);

  const msgAllValid = useMemo(
    () => !Object.keys(messageFormSchema).some((k) => runValidators(msgFormValues[k], messageFormSchema[k])),
    [msgFormValues]
  );

  const unreadCount = messages.filter(
    (m) => m.sender_type === 'client' && !m.read_by_staff
  ).length;

  useEffect(() => {
    if (typeof onUnreadChange === 'function') {
      onUnreadChange(unreadCount);
    }
  }, [unreadCount, onUnreadChange]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    if (!client?.id) return;
    try {
      const res = await enhancedApi.getChatMessages(client.id);
      const data = res.data || res;
      const list = data.messages || [];
      setMessages(list);
      if (!loading) scrollToBottom();
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [client?.id]);

  useEffect(() => {
    if (!client?.id) return;
    if (document.visibilityState !== 'visible') return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchMessages();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [client?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    const text = (messageText || '').trim();
    if (!client?.id) return;
    if (!msgValidateAll({ content: text })) return;
    setSending(true);
    try {
      const res = await enhancedApi.sendChatMessage(client.id, text);
      const data = res.data || res;
      const newMsg = data.message;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
        setMessageText('');
        msgClearErrors();
        scrollToBottom();
      }
    } catch (err) {
      const mapped = mapApiErrorsToForm(err.response?.data);
      if (Object.keys(mapped).length) msgMergeServerErrors(mapped);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMarkAllRead = async () => {
    if (!client?.id) return;
    try {
      await enhancedApi.markMessagesRead(client.id);
      setMessages((prev) =>
        prev.map((m) => (m.sender_type === 'client' ? { ...m, read_by_staff: true } : m))
      );
    } catch {
      // Silent
    }
  };

  const canSend = msgAllValid && !sending && client?.id;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500, flex: 1 }}>
      {unreadCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, flexShrink: 0 }}>
          <Button size="small" onClick={handleMarkAllRead}>
            Mark all read
          </Button>
        </Box>
      )}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          py: 1,
          minHeight: 0,
        }}
      >
        {loading ? (
          <Typography color="text.secondary">Loading...</Typography>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary">No messages yet. Start the conversation.</Typography>
        ) : (
          messages.map((msg) => {
            const isStaff = msg.sender_type === 'staff';
            const avatarLetter = (msg.sender_name || '?').charAt(0).toUpperCase();
            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: isStaff ? 'flex-end' : 'flex-start',
                  alignSelf: isStaff ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  alignItems: 'flex-end',
                  gap: 1,
                }}
              >
                {!isStaff && (
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', flexShrink: 0 }}>
                    {avatarLetter}
                  </Avatar>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isStaff ? 'flex-end' : 'flex-start' }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isStaff ? 'primary.light' : 'grey.100',
                      maxWidth: '100%',
                    }}
                  >
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      {msg.message}
                    </Typography>
                    {isStaff && msg.read_by_client != null && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.25,
                          color: msg.read_by_client ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {msg.read_by_client ? '✓✓' : '✓'}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formatTime(msg.timestamp)}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1.5,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          flexShrink: 0,
        }}
      >
        <TextField
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => {
            const v = e.target.value.slice(0, MAX_CHARS);
            setMessageText(v);
            if (msgTouched.content) msgValidateField('content', v);
          }}
          onBlur={() => msgTouchField('content', messageText)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          size="small"
          sx={{ flex: 1 }}
          error={Boolean(msgTouched.content && msgErrors.content)}
          helperText={fieldHelperText(msgTouched.content && msgErrors.content, MAX_CHARS, messageText.length)}
          inputProps={{ maxLength: MAX_CHARS }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={<SendIcon />}
          sx={{ flexShrink: 0 }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
}

export default MessagesTab;
